<div class="card">
  <div class="card-header bg-light">
      <h5 class="mb-0">Configuration details for Hub:  <strong>{{ hubId ? hubId : '' }} </strong></h5>
  </div>
  <div class="card-body">
    <!-- Message when no hub is selected -->
    <p *ngIf="!hubId">Select a hub to configure by clicking the "Configure" button next to a hub in the location details.</p>

    <!-- Loading indicator -->
    <div *ngIf="loading" class="text-center p-3">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading hub configuration...</p>
    </div>

    <!-- Error message -->
    <div *ngIf="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Clamp definitions loading/error messages -->
    <div *ngIf="clampDefinitionsLoading" class="alert alert-info">
      <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      Loading clamp definitions...
    </div>
    <div *ngIf="clampDefinitionsError" class="alert alert-warning">
      <strong>Warning:</strong> {{ clampDefinitionsError }}
      <p class="mb-0">Clamp definition UUIDs will be displayed instead of labels.</p>
    </div>

    <!-- Hub configuration details -->
    <div *ngIf="hubId && hubConfiguration && !loading && !error">
      <div class="row">
        <div class="col-md-6">
          <h6><u>Hub Details</u></h6>
          <p></p>
          <p><strong>ID:</strong> {{ hubConfiguration.id }}</p>
          <p><strong>Model Number:</strong> {{ hubConfiguration.model_number }} <span *ngIf="getIsRevenueGrade()" class="badge bg-success">Revenue Grade</span></p>
          <p><strong>Hardware Version:</strong> {{ hubConfiguration.hardware }}</p>
          <p><strong>Software Version:</strong> {{ hubConfiguration.software || 'N/A' }}</p>
          <p><strong>Created:</strong> {{ hubConfiguration.ts_created | date:'medium' }}</p>
          <p><strong>Modified:</strong> {{ hubConfiguration.ts_modified | date:'medium' }}</p>
        </div>

        <div class="col-md-6" *ngIf="hubConfiguration.installation">
          <h6><u>Installation Details</u></h6>
          <p></p>
          <p><strong>Label:</strong> {{ hubConfiguration.installation.label }}</p>
          <p><strong>State:</strong> {{ hubConfiguration.installation.state }}</p>
          <p><strong>Panel Type:</strong> {{ hubConfiguration.installation.panel_type }}</p>
          <p><strong>Sample Period:</strong> {{ hubConfiguration.installation.sample_period_ms }} ms</p>
          <p><strong>Samples Per Post:</strong> {{ hubConfiguration.installation.samples_per_post }}</p>
        </div>
      </div>

      <!-- WebSocket connection status -->
      <div *ngIf="hubId" class="mb-3 mt-3">
        <h6>Live Data Connection:</h6>
        <div *ngIf="wsConnected && connectionStatus === 'connected'" class="d-inline-block">
          <span class="badge bg-success">
            <div class="d-flex align-items-center">
              <div class="spinner-grow spinner-grow-sm text-light me-2" role="status">
                <span class="visually-hidden">Connected</span>
              </div>
              <div>Connected - Last data at: {{ getLastDataReceivedTimeFormatted() }}</div>
            </div>
          </span>
        </div>
        <div *ngIf="wsConnected && connectionStatus === 'warning'" class="d-inline-block">
          <span class="badge bg-warning">
            <div class="d-flex align-items-center">
              <div class="spinner-grow spinner-grow-sm text-dark me-2" role="status">
                <span class="visually-hidden">Warning</span>
              </div>
              <div>No data in 10s - Last data at: {{ getLastDataReceivedTimeFormatted() }}</div>
            </div>
          </span>
        </div>
        <div *ngIf="wsConnected && connectionStatus === 'danger'" class="d-inline-block">
          <span class="badge bg-danger">
            <div class="d-flex align-items-center">
              <div class="spinner-grow spinner-grow-sm text-light me-2" role="status">
                <span class="visually-hidden">Danger</span>
              </div>
              <div>No data in 20s - Last data at: {{ getLastDataReceivedTimeFormatted() }}</div>
            </div>
          </span>
        </div>
        <div *ngIf="!wsConnected && !wsError" class="d-inline-block">
          <span class="badge bg-warning">
            <div class="d-flex align-items-center">
              <div class="spinner-border spinner-border-sm text-dark me-2" role="status">
                <span class="visually-hidden">Connecting</span>
              </div>
              <div>Connecting to live data service...</div>
            </div>
          </span>
        </div>
        <div *ngIf="wsError" class="d-inline-block">
          <span class="badge bg-danger">
            <div class="d-flex align-items-center">
              <div class="me-2">⚠️</div>
              <div>Error: {{ wsError }}</div>
            </div>
          </span>
        </div>
      </div>

      <!-- Registers -->
      <div *ngIf="hubConfiguration.installation && hubConfiguration.installation.registers && hubConfiguration.installation.registers.length > 0">
        <h6 class="mt-4">Registers</h6>
        <div class="table-responsive">
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>Edit</th>
                <th>Label</th>
                <th>Type</th>
                <th>Clamp Definition</th>
                <th>Multiplier</th>
                <th>Inverted</th>
                <th>Phase Shift</th>
                <th>Power (W)</th>
                <th>Energy (Wh)</th>
                <th>Current (A)</th>
                <th>Voltage (V)</th>
                <th>Frequency (Hz)</th>
                <th>Power Factor</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let register of hubConfiguration.installation.registers; let i = index">
                <td>{{ i + 1 }}</td>
                <td>
                  <!-- Display mode: Edit button -->
                  <button *ngIf="!editMode || editingRegisterIndex !== i"
                          class="btn btn-sm btn-primary"
                          (click)="startEditingRegister(i)"
                          title="Edit register"
                          id="edit_register_{{ i }}"
                          >
                    Edit
                  </button>

                  <!-- Edit mode: Save/Cancel buttons -->
                  <div *ngIf="editMode && editingRegisterIndex === i" class="btn-group">
                    <button class="btn btn-sm btn-success"
                            (click)="saveRegister()"
                            [disabled]="savingRegister"
                            id="save_register_{{ i }}"
                            >
                      <span *ngIf="!savingRegister">Save</span>
                      <span class="spinner-border spinner-border-sm" *ngIf="savingRegister"></span>
                    </button>
                    <button class="btn btn-sm btn-secondary"
                            (click)="cancelEditingRegister()"
                            [disabled]="savingRegister"
                            id="cancel_edit_register_{{ i }}">
                      Cancel
                    </button>
                  </div>

                  <!-- Error message -->
                  <div *ngIf="editMode && editingRegisterIndex === i && registerError" class="text-danger small mt-1">
                    {{ registerError }}
                  </div>
                </td>
                <td>
                  <!-- Display mode (not editing) -->
                  <div *ngIf="editingRegisterIndex !== i">
                    <span>{{ register.label }}</span>
                  </div>

                  <!-- Edit mode for label in full edit mode -->
                    <div *ngIf="editingRegisterIndex === i && editMode" class="mb-2">
                    <input type="text" class="form-control form-control-sm"
                           id="labelInput{{i}}"
                           [(ngModel)]="editingRegisterLabel"
                           placeholder="Enter label"
                           [disabled]="savingRegister">
                  </div>

                  <!-- Error message for register editing -->
                  <div *ngIf="editingRegisterIndex === i && editMode && registerError" class="text-danger small mt-1">
                    {{ registerError }}
                  </div>
                </td>
                <td>
                  <!-- Display mode for Type -->
                  <span *ngIf="!editMode || editingRegisterIndex !== i" class="{{getCircuitDisplayClass(i)}}}">{{ getCircuitTypeLabel(i) }}</span>

                  <!-- Edit mode for Type -->
                  <div *ngIf="editMode && editingRegisterIndex === i" class="mb-2">
                    <select *ngIf="editMode && editingRegisterIndex === i" class="form-select form-select-sm" [(ngModel)]="editingCircuitType"
                      id="typeInput{{i}}"
                      >
                      <option *ngFor="let circuitType of getCircuitTypesFiltered(i)" [ngValue]="circuitType.id">
                        {{ circuitType.label }}
                      </option>
                    </select>
                  </div>
                </td>
                <td>
                  <!-- Display mode for Clamp Definition -->
                  <span *ngIf="!editMode || editingRegisterIndex !== i">{{ getClampDefinitionLabel(register.clamp_definition) }}</span>

                  <!-- Edit mode for Clamp Definition -->
                  <select *ngIf="editMode && editingRegisterIndex === i" class="form-select form-select-sm" [(ngModel)]="editingClampDefinition">
                    <option *ngFor="let clamp of getClampDefinitions(i)" [value]="clamp.uuid">
                      {{ clamp.label }}
                    </option>
                  </select>
                </td>
                <td>
                  <!-- Display mode for Multiplier -->
                  <span *ngIf="!editMode || editingRegisterIndex !== i">{{ register.multiplier }}</span>

                  <!-- Edit mode for Multiplier -->
                  <select *ngIf="editMode && editingRegisterIndex === i" class="form-select form-select-sm" [(ngModel)]="editingMultiplier">
                    <option [ngValue]="1">1</option>
                    <option [ngValue]="2">2</option>
                  </select>
                </td>
                <td>
                  <!-- Display mode for Inverted -->
                  <span *ngIf="!editMode || editingRegisterIndex !== i">{{ register.inverted ? 'Yes' : 'No' }}</span>

                  <!-- Edit mode for Inverted -->
                  <div *ngIf="editMode && editingRegisterIndex === i" class="form-check">
                    <input class="form-check-input" type="checkbox" [(ngModel)]="editingInverted" id="invertedCheck{{i}}" [disabled]="!isInvertedFieldEditable(i)">
                    <label class="form-check-label" for="invertedCheck{{i}}">Inverted</label>
                    <small *ngIf="!isInvertedFieldEditable(i)" class="text-muted d-block">
                      Not editable for this register type
                    </small>
                  </div>
                </td>
                <td>{{ register.phase_shift }}</td>
                <td>{{ getPower(i) }}
                  <div *ngIf="getPowerPercent(i)>= 0" class="progress">
                    <div class="progress-bar bg-success" role="progressbar" [style.width.%]="Math.abs(getPowerPercent(i))" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>
                  <div *ngIf="getPowerPercent(i)< 0"  class="progress">
                    <div class="progress-bar bg-danger" role="progressbar" [style.width.%]="Math.abs(getPowerPercent(i))" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>
                </td>
                <td>{{ getEnergy(i) }}
                    <div *ngIf="getEnergyPercent(i)>= 0" class="progress">
                        <div class="progress-bar bg-success" role="progressbar" [style.width.%]="Math.abs(getEnergyPercent(i))" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div *ngIf="getEnergyPercent(i)< 0"  class="progress">
                        <div class="progress-bar bg-danger" role="progressbar" [style.width.%]="Math.abs(getEnergyPercent(i))" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                </td>
                <td>{{ getCurrent(i) }}</td>
                <td>{{ getVoltage(i) }}</td>
                <td>{{ getFrequency(i) }}</td>
                <td>{{ getPowerFactor(i) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- No registers message -->
      <div *ngIf="!hubConfiguration.installation || !hubConfiguration.installation.registers || hubConfiguration.installation.registers.length === 0" class="alert alert-info mt-3">
        No registers found for this hub.
      </div>

      <!-- Live data -->
      <div class="card mt-2">
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <h6 class="mb-0">Live Data</h6>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="showRawLiveDataCheck" [(ngModel)]="showRawLiveData">
            <label class="form-check-label" for="showRawLiveDataCheck">Show Raw Live Data</label>
          </div>
        </div>
        <div class="card-body" *ngIf="wsConnected && wsData && showRawLiveData">
          <pre class="mb-0">{{ wsData | json }}</pre>
        </div>
      </div>
    </div>
  </div>

</div>
